import { useEffect, useRef, useState } from 'react';
import type { ContextUsage } from '@shared/protocol';

interface Props {
  context?: ContextUsage;
  /** Compaction runs as a turn, so it is offered only while nothing else is. */
  canCompact: boolean;
  onCompact: () => void;
}

/** 68213 -> "68.2k", 1000000 -> "1M". */
function tokens(n: number): string {
  if (n >= 1_000_000) return `${+(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1000) return `${+(n / 1000).toFixed(1)}k`;
  return String(n);
}

function level(pct: number): 'ok' | 'warn' | 'high' {
  return pct >= 90 ? 'high' : pct >= 70 ? 'warn' : 'ok';
}

/**
 * Top-bar chip showing how full the context window is, with a breakdown on
 * click. The figure is the CLI's own estimate after the last turn; it does not
 * move mid-turn.
 */
export function ContextMeter({ context, canCompact, onCompact }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const pct = context && context.maxTokens > 0 ? Math.round((context.totalTokens / context.maxTokens) * 100) : undefined;
  const inWindow = context?.categories.filter((c) => c.kind !== 'deferred') ?? [];
  const deferred = context?.categories.filter((c) => c.kind === 'deferred') ?? [];

  return (
    <div className="ctx" ref={rootRef}>
      <button
        className={`chip chip__mono ctx__chip ctx--${pct === undefined ? 'ok' : level(pct)}`}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        title={context ? `Context: ${tokens(context.totalTokens)} / ${tokens(context.maxTokens)} tokens` : 'Context usage'}
      >
        <span className="ctx__gauge" aria-hidden>
          <span style={{ transform: `scaleX(${Math.min(1, (pct ?? 0) / 100)})` }} />
        </span>
        {pct === undefined ? 'ctx —' : `${pct}%`}
      </button>

      {open && (
        <div className="ctx__pop" role="dialog" aria-label="Context usage">
          <div className="ctx__head">
            <strong>Context window</strong>
            {context && (
              <span className="chip__mono">
                {tokens(context.totalTokens)} / {tokens(context.maxTokens)}
              </span>
            )}
          </div>

          {!context ? (
            <p className="ctx__empty">Shown after Claude finishes its first turn in this room.</p>
          ) : (
            <>
              <div className="ctx__bar" aria-hidden>
                {inWindow.map((c) => (
                  <span
                    key={c.name}
                    className={`ctx__seg ctx__seg--${c.kind}`}
                    style={{ flexGrow: c.tokens }}
                    title={`${c.name}: ${tokens(c.tokens)}`}
                  />
                ))}
              </div>

              <ul className="ctx__rows">
                {inWindow.map((c) => (
                  <li key={c.name}>
                    <span className={`ctx__swatch ctx__seg--${c.kind}`} />
                    <span className="ctx__name">{c.name}</span>
                    <span className="chip__mono">{tokens(c.tokens)}</span>
                  </li>
                ))}
                {deferred.map((c) => (
                  <li key={c.name} className="ctx__row--deferred" title="Loaded on demand; not in the window">
                    <span className="ctx__swatch" />
                    <span className="ctx__name">{c.name}</span>
                    <span className="chip__mono">{tokens(c.tokens)}</span>
                  </li>
                ))}
              </ul>

              <div className="ctx__foot">
                <span>Updated {new Date(context.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                <button
                  className="button"
                  disabled={!canCompact}
                  title={canCompact ? 'Runs /compact for everyone in the room' : 'Wait for the current turn to finish'}
                  onClick={() => {
                    onCompact();
                    setOpen(false);
                  }}
                >
                  Compact now
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
